import { useState, useMemo, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  MenuItem,
  TablePagination,
  CircularProgress,
  Alert,
  Snackbar,
  Tooltip,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
} from "@mui/icons-material";
import DashboardLayout from "@/components/DashboardLayout";
import * as benutzerApi from "@/api/benutzer";
import * as kundeApi from "@/api/kunde";
import type { BenutzerResponseDTO, KundeResponseDTO } from "@/api/types";

interface User {
  id: number;
  username: string;
  email: string;
  kundeId: number;
  kundeName: string;
  ik: string;
  isAdmin: boolean;
}

const Users = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [customers, setCustomers] = useState<KundeResponseDTO[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [formData, setFormData] = useState({
    kundeId: 0,
    username: "",
    email: "",
  });
  const [adminConfirmUser, setAdminConfirmUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" as "success" | "error" });

  const [usernameFilter, setUsernameFilter] = useState("");
  const [emailFilter, setEmailFilter] = useState("");
  const [customerFilter, setCustomerFilter] = useState<string>("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Load users and customers
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const [usersData, customersData] = await Promise.all([
          benutzerApi.getAll(),
          kundeApi.getAll(),
        ]);
        
        setCustomers(customersData);

        // Map backend response to User interface
        const mappedUsers: User[] = usersData
          .filter((benutzer) => benutzer && benutzer.id)
          .map((benutzer) => ({
            id: benutzer.id,
            username: benutzer.username || "",
            email: benutzer.email || "",
            kundeId: benutzer.kundeId || 0,
            kundeName: benutzer.kundeName || "",
            ik: benutzer.ik || "",
            isAdmin: benutzer.isAdmin || false,
          }));
        
        setUsers(mappedUsers);
      } catch (err) {
        console.error("Error loading data:", err);
        setError("Fehler beim Laden der Daten");
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, []);

  const filteredUsers = useMemo(() => {
    return users.filter((user) => {
      const matchesUsername = !usernameFilter || (user.username || "").toLowerCase().includes(usernameFilter.toLowerCase());
      const matchesEmail = !emailFilter || (user.email || "").toLowerCase().includes(emailFilter.toLowerCase());
      const matchesCustomer = customerFilter === "all" || String(user.kundeId) === customerFilter;
      return matchesUsername && matchesEmail && matchesCustomer;
    });
  }, [users, usernameFilter, emailFilter, customerFilter]);

  const paginatedUsers = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredUsers.slice(start, start + rowsPerPage);
  }, [filteredUsers, page, rowsPerPage]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleAddNew = () => {
    setEditingUser(null);
    setFormData({
      kundeId: 0,
      username: "",
      email: "",
    });
    setIsDialogOpen(true);
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    setFormData({
      kundeId: user.kundeId,
      username: user.username,
      email: user.email,
    });
    setIsDialogOpen(true);
  };

  const handleToggleAdmin = async () => {
    if (!adminConfirmUser) return;
    try {
      const updated = await benutzerApi.toggleAdmin(adminConfirmUser.id);
      setUsers(users.map((u) => (u.id === updated.id ? { ...u, isAdmin: updated.isAdmin } : u)));
      setSnackbar({
        open: true,
        message: updated.isAdmin
          ? `Administrator-Rechte für "${updated.username}" wurden gesetzt.`
          : `Administrator-Rechte für "${updated.username}" wurden entfernt.`,
        severity: "success",
      });
    } catch (err: any) {
      console.error("Error toggling admin:", err);
      setSnackbar({ open: true, message: err.message || "Fehler beim Ändern der Administrator-Rechte", severity: "error" });
    } finally {
      setAdminConfirmUser(null);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm("Möchten Sie diesen Benutzer wirklich löschen?")) {
      return;
    }
    
    try {
      await benutzerApi.remove(id);
      setUsers(users.filter((u) => u.id !== id));
      setSnackbar({ open: true, message: "Benutzer erfolgreich gelöscht", severity: "success" });
    } catch (err: any) {
      console.error("Error deleting user:", err);
      const errorMsg = err.message || "Fehler beim Löschen des Benutzers";
      setSnackbar({ open: true, message: errorMsg, severity: "error" });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (editingUser) {
        const updated = await benutzerApi.update(editingUser.id, {
          username: formData.username,
          email: formData.email,
          kundeId: formData.kundeId,
        });
        
        const updatedUser: User = {
          id: updated.id,
          username: updated.username,
          email: updated.email,
          kundeId: updated.kundeId,
          kundeName: updated.kundeName,
          ik: updated.ik,
          isAdmin: updated.isAdmin,
        };
        
        setUsers(users.map((u) => (u.id === editingUser.id ? updatedUser : u)));
        setSnackbar({ open: true, message: "Benutzer erfolgreich aktualisiert", severity: "success" });
      } else {
        const created = await benutzerApi.create({
          username: formData.username,
          email: formData.email,
          kundeId: formData.kundeId,
        });
        
        const newUser: User = {
          id: created.id,
          username: created.username,
          email: created.email,
          kundeId: created.kundeId,
          kundeName: created.kundeName,
          ik: created.ik,
          isAdmin: created.isAdmin,
        };
        
        setUsers([...users, newUser]);
        setSnackbar({ open: true, message: "Benutzer erfolgreich erstellt. Das initiale Passwort wurde per E-Mail versandt.", severity: "success" });
      }
      
      setIsDialogOpen(false);
    } catch (err: any) {
      console.error("Error saving user:", err);
      const errorMsg = err.message || "Fehler beim Speichern des Benutzers";
      setSnackbar({ open: true, message: errorMsg, severity: "error" });
    }
  };

  return (
    <DashboardLayout>
      <Box sx={{ maxWidth: 1400, mx: "auto" }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
        }}
      >
        <Typography variant="h4" component="h1" fontWeight="bold">
          Benutzerverwaltung
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddNew}
          size="large"
          sx={{ color: "white" }}
        >
          Neuer Benutzer
        </Button>
      </Box>

      {/* Filter */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Filter
        </Typography>
        <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 2 }}>
          <TextField
            label="Benutzername suchen"
            placeholder="Benutzername eingeben..."
            value={usernameFilter}
            onChange={(e) => { setUsernameFilter(e.target.value); setPage(0); }}
            fullWidth
          />
          <TextField
            label="E-Mail suchen"
            placeholder="E-Mail eingeben..."
            value={emailFilter}
            onChange={(e) => { setEmailFilter(e.target.value); setPage(0); }}
            fullWidth
          />
          <TextField
            select
            label="Kunde"
            value={customerFilter}
            onChange={(e) => { setCustomerFilter(e.target.value); setPage(0); }}
            fullWidth
          >
            <MenuItem value="all">Alle</MenuItem>
            {customers.map((customer) => (
              <MenuItem key={customer.id} value={String(customer.id)}>
                {customer.name}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      </Paper>

      {/* Table */}
      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      )}
      
      {error && !loading && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {!loading && !error && (
      <Paper>
        <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Kunde</strong></TableCell>
                  <TableCell><strong>Benutzername</strong></TableCell>
                  <TableCell><strong>E-Mail</strong></TableCell>
                  <TableCell><strong>IK</strong></TableCell>
                  <TableCell align="center"><strong>Admin</strong></TableCell>
                  <TableCell align="right"><strong>Aktionen</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                      <Typography color="text.secondary">
                        {users.length === 0
                          ? "Keine Benutzer vorhanden"
                          : "Keine Benutzer entsprechen den Filterkriterien"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedUsers.map((user) => (
                    <TableRow key={user.id} hover>
                      <TableCell>{user.kundeName}</TableCell>
                      <TableCell>{user.username}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>{user.ik}</TableCell>
                      <TableCell align="center">
                        <Tooltip title={user.isAdmin ? "Admin – klicken zum Entfernen" : "Kein Admin – klicken zum Setzen"}>
                          <IconButton
                            size="small"
                            onClick={() => setAdminConfirmUser(user)}
                            color={user.isAdmin ? "success" : "default"}
                          >
                            {user.isAdmin ? (
                              <AdminPanelSettingsIcon fontSize="small" />
                            ) : (
                              <CancelIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(user)}
                          color="primary"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(user.id)}
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredUsers.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage="Zeilen pro Seite:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} von ${count}`
          }
        />
      </Paper>
      )}
      </Box>

      {/* Dialog */}
      <Dialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingUser ? "Benutzer bearbeiten" : "Neuen Benutzer anlegen"}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
              <TextField
                select
                label="Kunde"
                value={formData.kundeId || ""}
                onChange={(e) => {
                  const newKundeId = Number(e.target.value);
                  const newIk = customers.find((c) => c.id === newKundeId)?.ik ?? "";
                  setFormData({
                    ...formData,
                    kundeId: newKundeId,
                    ...(!editingUser && { username: newIk ? `${newIk}-` : "" }),
                  });
                }}
                required
                fullWidth
              >
                {customers.map((customer) => (
                  <MenuItem key={customer.id} value={customer.id}>
                    {customer.name} ({customer.ik})
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Benutzername"
                type="text"
                value={formData.username}
                onChange={(e) =>
                  setFormData({ ...formData, username: e.target.value })
                }
                required
                fullWidth
              />
              <TextField
                label="E-Mail"
                type="email"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                required
                fullWidth
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsDialogOpen(false)}>Abbrechen</Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={<SaveIcon />}
              sx={{ color: "white" }}
            >
              {editingUser ? "Speichern" : "Anlegen"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Admin toggle confirmation dialog */}
      <Dialog
        open={adminConfirmUser !== null}
        onClose={() => setAdminConfirmUser(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <AdminPanelSettingsIcon color="warning" />
          Administrator-Rechte ändern
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Diese Aktion verändert die Berechtigungen des Benutzers.
          </Alert>
          <Typography>
            {adminConfirmUser?.isAdmin
              ? `Möchten Sie die Administrator-Rechte von "${adminConfirmUser?.username}" wirklich entfernen?`
              : `Möchten Sie "${adminConfirmUser?.username}" wirklich als Administrator festlegen?`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdminConfirmUser(null)}>Abbrechen</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleToggleAdmin}
            sx={{ color: "white" }}
          >
            Bestätigen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
};

export default Users;
